'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import { VariantProps } from 'cva'
import { ComponentProps, createContext, useContext } from 'react'

import { cn } from '../../lib/util'

import { tabListStyle, tabsTriggerStyle } from './tabs.css'

const TabContext = createContext<VariantProps<typeof tabListStyle> | null>(null)

const useTabContext = (): VariantProps<typeof tabListStyle> => {
  const context = useContext(TabContext)

  if (!context) {
    throw new Error('TabContext 내부에서만 useTabContext를 사용할 수 있습니다.')
  }

  return { variants: context.variants ?? 'flexible' }
}

const Tabs = ({
  variants = 'flexible',
  ...props
}: ComponentProps<typeof TabsPrimitive.Root> & VariantProps<typeof tabListStyle>) => {
  return (
    <TabContext.Provider value={{ variants }}>
      <TabsPrimitive.Root {...props} />
    </TabContext.Provider>
  )
}

const TabsList = ({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) => {
  const { variants } = useTabContext()
  return <TabsPrimitive.List className={cn(tabListStyle({ variants }), className)} {...props} />
}

const TabsTrigger = ({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Trigger> & VariantProps<typeof tabsTriggerStyle>) => {
  const { variants } = useTabContext()
  return <TabsPrimitive.Trigger className={cn(tabsTriggerStyle({ variants }), className)} {...props} />
}

const TabsContent = ({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    className={cn(
      [
        // 스타일링
        'ring-offset-background',
        'focus-visible:outline-none',
        'focus-visible:ring-2',
        'focus-visible:ring-ring',
        'focus-visible:ring-offset-2',
      ],
      className,
    )}
    {...props}
  />
)

TabsList.displayName = TabsPrimitive.List.displayName
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
